import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BulkArchiveProjectsInput,
  BulkResult,
  BulkUpdateProjectStatusInput,
  CreateProjectInput,
  ImportProjectReferenceDataInput,
  ItemResponse,
  ListResponse,
  ProjectEconomicsResponse,
  ProjectFacets,
  ProjectGroup,
  ProjectResponse,
  RecordAcceptanceInput,
  UpdateProjectInput,
} from '@proerp/shared';
import { apiRequest, collectAllPages } from '@/lib/apiClient';

const PROJECTS_KEY = ['projects'] as const;

export interface ProjectsQuery {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** Nur Projekte dieses Kunden (CRM-Verknüpfung). */
  customerId?: string;
  /** true = auch archivierte (soft-gelöschte) Projekte anzeigen (§7). */
  includeArchived?: boolean;
  // Referenzen-Ansicht (Vertrieb): zusätzliche Filter auf denselben Projektdaten.
  /** Land-Mehrfachauswahl. */
  countries?: string[];
  plantType?: string;
  product?: string;
  projectGroup?: ProjectGroup;
  /** Baujahr-Zeitraum: Treffer, wenn Start- ODER Liefertermin im Bereich liegt. */
  yearFrom?: number;
  yearTo?: number;
  lengthMin?: number;
  lengthMax?: number;
  widthMin?: number;
  widthMax?: number;
  heightMin?: number;
  heightMax?: number;
}

/** Exportiert für den PDF-Export-Button (`ReferencesPage.tsx`) — gleiche Filter wie die Liste. */
export function buildQueryString(query: ProjectsQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page ?? 1));
  params.set('pageSize', String(query.pageSize ?? 50));
  if (query.sort) params.set('sort', query.sort);
  if (query.q) params.set('q', query.q);
  if (query.customerId) params.set('customerId', query.customerId);
  if (query.includeArchived) params.set('includeArchived', 'true');
  if (query.countries?.length) params.set('countries', query.countries.join(','));
  if (query.plantType) params.set('plantType', query.plantType);
  if (query.product) params.set('product', query.product);
  if (query.projectGroup) params.set('projectGroup', query.projectGroup);
  if (query.yearFrom !== undefined) params.set('yearFrom', String(query.yearFrom));
  if (query.yearTo !== undefined) params.set('yearTo', String(query.yearTo));
  if (query.lengthMin !== undefined) params.set('lengthMin', String(query.lengthMin));
  if (query.lengthMax !== undefined) params.set('lengthMax', String(query.lengthMax));
  if (query.widthMin !== undefined) params.set('widthMin', String(query.widthMin));
  if (query.widthMax !== undefined) params.set('widthMax', String(query.widthMax));
  if (query.heightMin !== undefined) params.set('heightMin', String(query.heightMin));
  if (query.heightMax !== undefined) params.set('heightMax', String(query.heightMax));
  return params.toString();
}

/** Projektliste (CLAUDE.md §8: page/pageSize/sort/q). */
export function useProjects(query: ProjectsQuery) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, query],
    queryFn: () =>
      apiRequest<ListResponse<ProjectResponse>>(`/v1/projects?${buildQueryString(query)}`),
  });
}

/** Nicht-Hook-Fetch einer Projekt-Seite (für „alle laden" via collectAllPages). */
export function fetchProjectsPage(query: ProjectsQuery): Promise<ListResponse<ProjectResponse>> {
  return apiRequest<ListResponse<ProjectResponse>>(`/v1/projects?${buildQueryString(query)}`);
}

/**
 * Lädt ALLE Projekte (flaches Array) für Einzelauswahlen/Filter — ein fixes `pageSize`
 * würde Projekte jenseits des Deckels unwählbar machen (Projekte wachsen monoton, §12).
 * Standardmäßig neueste zuerst; `includeArchived` optional. Muster wie `useAllArticles`.
 */
export function useAllProjects(options?: { includeArchived?: boolean }): ReturnType<typeof useQuery<ProjectResponse[]>> {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'all', { includeArchived }],
    queryFn: () =>
      collectAllPages((page, pageSize) =>
        fetchProjectsPage({ page, pageSize, sort: '-createdAt', includeArchived }),
      ),
  });
}

/** Einzelnes Projekt (Detail-Seite). */
export function useProject(id: string) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      apiRequest<ItemResponse<ProjectResponse>>('/v1/projects', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

export function useUpdateProject(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) =>
      apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
      // Zeitplan-Seite zeigt Liefertermin-Marker des Projekts — mit aktualisieren.
      void queryClient.invalidateQueries({ queryKey: ['project-schedule'] });
    },
  });
}

/**
 * Projekt archivieren (Soft-Delete, §7). Das Backend lehnt mit 409 ab, wenn noch
 * nicht stornierte Belege bzw. offene Posten am Projekt hängen (#31). Admins können
 * mit `force: true` trotzdem archivieren (Altbestand) — Backend prüft `admin.manage`.
 */
export function useArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      apiRequest<{ data: { id: string } }>(
        `/v1/projects/${id}${force ? '?force=true' : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Sammel-Statuswechsel: Status mehrerer Projekte gemeinsam setzen. */
export function useBulkUpdateProjectStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkUpdateProjectStatusInput): Promise<BulkResult> =>
      (
        await apiRequest<{ data: BulkResult }>('/v1/projects/bulk-status', {
          method: 'POST',
          body: JSON.stringify(input),
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Sammel-Archivierung. `force` (nur Admins) archiviert auch bei offenen Belegen. */
export function useBulkArchiveProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkArchiveProjectsInput): Promise<BulkResult> =>
      (
        await apiRequest<{ data: BulkResult }>('/v1/projects/bulk-archive', {
          method: 'POST',
          body: JSON.stringify(input),
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Archiviertes Projekt wieder aktivieren (Soft-Delete zurücknehmen). */
export function useReactivateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}/reactivate`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Projekt abschließen (Status GESCHLOSSEN) — fachlicher Abschluss, kein Soft-Delete. */
export function useCloseProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}/close`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/** Abnahme erfassen (Schritt 11). */
export function useRecordAcceptance(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordAcceptanceInput) =>
      apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}/acceptance`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

export function useRevokeAcceptance(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<ProjectResponse>>(`/v1/projects/${id}/acceptance`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}

/**
 * Wirtschaftlichkeit eines Projekts (Auftragswert, Marge, Fakturierungsgrad).
 * Serverseitig hinter `analytics.read` (ADR 0037) — `isEnabled` erlaubt es,
 * den Request ohne Berechtigung gar nicht erst abzusetzen.
 */
export function useProjectEconomics(id: string, isEnabled = true) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'economics', id],
    queryFn: () =>
      apiRequest<ItemResponse<ProjectEconomicsResponse>>(`/v1/projects/${id}/economics`),
    enabled: Boolean(id) && isEnabled,
  });
}

// Die alten Plan-/Controlling-Hooks (ADR 0016) sind mit der Kalkulation
// (ADR 0055, calculationApi.ts) entfallen — Zeilen laufen über
// useProjectCalculation/useSaveCalcLines/useDeriveCalculationFromOrders.

/** Verfügbare Filterwerte (Land/Anlagentyp/Produkt) für die Referenzen-Ansicht. */
export function useProjectFacets() {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'facets'],
    queryFn: () => apiRequest<ItemResponse<ProjectFacets>>('/v1/projects/facets'),
  });
}

/** „Ähnliche Referenzen": gleiches Land + gleicher Anlagentyp (bis zu 5 Treffer). */
export function useSimilarProjects(id: string) {
  return useQuery({
    queryKey: [...PROJECTS_KEY, 'detail', id, 'similar'],
    queryFn: () => apiRequest<ListResponse<ProjectResponse>>(`/v1/projects/${id}/similar`),
    enabled: Boolean(id),
  });
}

/**
 * Nachträgliche Anreicherung bestehender Projekte mit historischen
 * Referenzdaten (Base44-Migration, Backfill) — Match per Projekt-Code.
 */
export function useImportProjectReferenceData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportProjectReferenceDataInput): Promise<BulkResult> =>
      (
        await apiRequest<{ data: BulkResult }>('/v1/projects/import-reference-data', {
          method: 'POST',
          body: JSON.stringify(input),
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROJECTS_KEY }),
  });
}
